import RoutedTabs from '@Components/UI/Tabs/Tabs';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexLayout, Spacer } from '@Styled/utilities';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Chip } from '@mui/material';
import { rgba } from 'polished';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { apiData, errorMessage } from '../Subscriptions/subscriptions.shared';
import PerformanceOverview, { Overview } from './PerformanceOverview';
import { PerfAttendance, PerfTests, PerfWebinars } from './PerformanceTables';

export type ReportTarget = { studentId: number; type: 'standalone' | 'bundle'; id: number };

const HASHES = ['overview', 'attendance', 'tests', 'webinars'];

/**
 * One subscription's report: Overview · Attendance · Tests · Webinars
 * (the classroom report's layout). A bundle's tables span all its member
 * courses and offer a member-course filter.
 */
const PerformanceReport: React.FC<{ studentId: number }> = ({ studentId }) => {
	const { type, subId } = useParams<{ type: string; subId: string }>();
	const { url } = useRouteMatch();
	const { pathname } = useLocation();
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const [overview, setOverview] = useState<Overview>();
	const target: ReportTarget = useMemo(
		() => ({ studentId, type: type === 'bundle' ? 'bundle' : 'standalone', id: Number(subId) }),
		[studentId, type, subId]
	);
	// …/performance — lets a bundle's member row open that course's report
	const performanceBase = url.slice(0, url.length - `/${type}/${subId}`.length);

	useEffect(() => {
		let cancelled = false;
		setOverview(undefined);
		api
			.post(`user/profile/${studentId}/performance/overview`, { type: target.type, id: target.id })
			.then((response) => !cancelled && setOverview(apiData<Overview>(response)))
			.catch((error) => !cancelled && displaySnackbar('error', errorMessage(error, 'Unable to load the report')));
		return () => {
			cancelled = true;
		};
	}, [studentId, target.type, target.id, displaySnackbar]);

	const tabValue = HASHES.find((hash) => pathname.endsWith(`/${hash}`)) ?? 'overview';

	return (
		<>
			<FlexLayout justifyContent="flex-start" gridGap="0.8rem" alignItems="center">
				<BackButton
					alignItems="center"
					justifyContent="center"
					onClick={() => history.push({ pathname: performanceBase })}
				>
					<BackIcon />
				</BackButton>
				<Header>{overview?.subscription?.title ?? ''}</Header>
				<Chip
					size="small"
					variant="outlined"
					color={target.type === 'bundle' ? 'secondary' : 'primary'}
					label={target.type === 'bundle' ? 'Bundle' : 'Course'}
				/>
			</FlexLayout>
			{/* keyed: a bundle member row swaps the report in place */}
			<Container key={`${target.type}-${target.id}`}>
				<RoutedTabs baseURL={url} withRouter>
					<RoutedTabs.Tabs
						$hashPrefix="#"
						hashes={HASHES}
						onChange={() => {}}
						orientation="vertical"
						value={tabValue}
					>
						<RoutedTabs.Item
							value="overview"
							label={<RoutedTabs.ItemTitle>Overview</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon mIconType="Outlined">dashboard</RoutedTabs.ItemIcon>}
						/>
						<RoutedTabs.Item
							value="attendance"
							label={<RoutedTabs.ItemTitle>Attendance</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon mIconType="Outlined">contact_page</RoutedTabs.ItemIcon>}
						/>
						<RoutedTabs.Item
							value="tests"
							label={<RoutedTabs.ItemTitle>TESTS</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon mIconType="Outlined">live_help</RoutedTabs.ItemIcon>}
						/>
						<RoutedTabs.Item
							value="webinars"
							label={<RoutedTabs.ItemTitle>WEBINARS</RoutedTabs.ItemTitle>}
							icon={<RoutedTabs.ItemIcon mIconType="Outlined">podcasts</RoutedTabs.ItemIcon>}
						/>
					</RoutedTabs.Tabs>
					<Spacer mx={'0.625rem'} />
					<RoutedTabs.Content value={tabValue}>
						<RoutedTabs.ContentItem hash="overview">
							<PerformanceOverview
								overview={overview}
								onOpenCourse={(courseId) =>
									history.push({ pathname: `${performanceBase}/standalone/${courseId}/overview` })
								}
							/>
						</RoutedTabs.ContentItem>
						<RoutedTabs.ContentItem hash="attendance">
							<PerfAttendance target={target} />
						</RoutedTabs.ContentItem>
						<RoutedTabs.ContentItem hash="tests">
							<PerfTests target={target} />
						</RoutedTabs.ContentItem>
						<RoutedTabs.ContentItem hash="webinars">
							<PerfWebinars target={target} />
						</RoutedTabs.ContentItem>
					</RoutedTabs.Content>
				</RoutedTabs>
			</Container>
		</>
	);
};

const Container = styled.div`
	padding: 1.75rem 1.625rem;
	border-radius: 10px;
	border: 1px solid ${rgba('#5ac0fc', 0.25)};
`;
const Header = styled.p`
	font-size: 1.25rem;
	font-weight: bold;
	color: #251b33;
	margin: 1.563rem 0;
`;
const BackButton = styled(FlexLayout)`
	width: 2rem;
	height: 2rem;
	border-radius: 50%;
	cursor: pointer;
`;
const BackIcon = styled(ChevronLeftIcon)`
	font-size: 2rem;
	color: ${(props) => props.theme.palette.common.black};
`;

export default PerformanceReport;
